import React from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ClipboardList, MapPin, User, LogOut } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useCustomer } from "@/context/CustomerContext";
import StoreShell from "@/components/StoreShell";
import { Splash, ErrorView } from "../Storefront";
import { Card, hexA, initials } from "./AccountUI";

// "My Account" shell: banner, profile card and pill tabs; the page for the active tab renders in the Outlet.
export default function AccountLayout() {
  const s = useStore();
  const cu = useCustomer();
  const nav = useNavigate();
  const loc = useLocation();
  if (s.loading || !cu.ready) return <Splash />;
  if (s.error) return <ErrorView error={s.error} />;
  if (!cu.customer) return <Navigate to={`/store/${s.slug}/account/login`} replace state={{ from: loc.pathname }} />;
  const { c, font, border } = s;
  const base = `/store/${s.slug}/account`;
  const tabs = [["orders", "Orders", ClipboardList], ["addresses", "Addresses", MapPin], ["profile", "Profile", User]];
  const logout = () => { cu.signOut(); nav(`/store/${s.slug}`); };

  return (
    <StoreShell>
      <div style={{ background: `linear-gradient(120deg, ${c.primary}, ${hexA(c.primary, 0.82)} 55%, ${c.accent})`, color: "#fff" }}>
        <div className="max-w-6xl mx-auto px-5 py-10 md:py-14">
          <nav className="text-sm opacity-80" aria-label="Breadcrumb"><Link to={`/store/${s.slug}`} className="hover:underline">Home</Link> <span className="mx-1.5">/</span> My account</nav>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold" style={{ fontFamily: font }} data-testid="account-title">My Account</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 -mt-6 pb-14" style={{ fontFamily: font }}>
        <Card s={s} className="p-5 flex items-center gap-4" data-testid="account-profile-card">
          <span className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white shrink-0" style={{ background: `linear-gradient(135deg, ${c.primary}, ${c.accent})` }}>{initials(cu.customer.name)}</span>
          <div className="min-w-0"><div className="font-bold text-lg truncate" data-testid="account-name">{cu.customer.name}</div><div className="text-sm truncate" style={{ color: s.muted }}>+91 {cu.customer.mobile}{cu.customer.email ? ` · ${cu.customer.email}` : ""}</div></div>
        </Card>

        <Card s={s} className="mt-4 p-2 flex flex-wrap items-center gap-1" role="tablist" data-testid="account-tabs">
          {tabs.map(([key, label, Icon]) => (
            <NavLink key={key} to={`${base}/${key}`} data-testid={`account-tab-${key}`} className="px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors"
              style={({ isActive }) => (isActive ? { background: c.primary, color: "#fff" } : { color: c.text })}><Icon size={16} /> {label}</NavLink>
          ))}
          <button onClick={logout} data-testid="account-logout" className="ml-auto px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 text-red-600 hover:bg-red-50"><LogOut size={16} /> Log out</button>
        </Card>

        <div className="mt-8"><Outlet /></div>
      </div>
    </StoreShell>
  );
}
